import { Stack, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { Platform, View, StyleSheet } from "react-native";
import { ActivityIndicator, Button, Text } from "react-native-paper";

import { ContextVisibilityProvider } from "@/components/visit/ContextVisibility";
import { VisitDocumentViewerProvider } from "@/components/visit/VisitDocumentViewer";
import { fetchVisitForm, type FormDefinition } from "@/lib/api";
import { FormProvider } from "@/lib/FormContext";

export default function VisitLayout() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const [form, setForm] = useState<FormDefinition | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadForm = useCallback(async () => {
        if (!id) {
            return;
        }
        try {
            const data = await fetchVisitForm(id);
            setForm(data);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load visit");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    // Expo Router's Stack on web keeps inactive screens mounted as position:absolute
    // overlays with aria-hidden="true". Two problems compound:
    //
    // 1. OVERLAY BLOCKING: React Navigation sets pointerEvents="none" as a PROP on
    //    hidden containers, but RNW ignores this deprecated prop. Hidden screens sit
    //    on top with pointer-events:auto, intercepting clicks. Fixed by injecting a
    //    CSS rule — unlike inline styles (Attempt 4), CSS auto-removes when the
    //    attribute is gone, so it's safe during transitions.
    //
    // 2. STALE FOCUS: Focused elements inside hidden screens steal the first click
    //    (browser transfers focus instead of firing onPress). Fixed by blurring on
    //    every pointerdown (captures existing stale focus) and via MutationObserver
    //    (catches focus set during/after transitions).
    //
    // See docs/prototypes/welcome-package-click-bug.md for full investigation.
    useEffect(() => {
        if (Platform.OS !== "web") {
            return;
        }

        // Fix 1: CSS rule prevents hidden screen overlays from capturing clicks
        const style = document.createElement("style");
        style.textContent =
            '[aria-hidden="true"] { pointer-events: none !important; } #visit-section-scroll { overflow-anchor: none; }';
        document.head.appendChild(style);

        // Fix 2a: Blur stale focus on every pointerdown (before click processing)
        const onPointerDown = () => {
            const focused = document.activeElement;
            if (
                focused instanceof HTMLElement &&
                focused !== document.body &&
                focused.closest('[aria-hidden="true"]')
            ) {
                focused.blur();
            }
        };
        document.addEventListener("pointerdown", onPointerDown, true);

        // Fix 2b: Blur focus whenever a screen becomes hidden (catches focus set
        // by Expo Router after transitions)
        const observer = new MutationObserver(() => {
            const focused = document.activeElement;
            if (
                focused instanceof HTMLElement &&
                focused !== document.body &&
                focused.closest('[aria-hidden="true"]')
            ) {
                focused.blur();
            }
        });
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["aria-hidden"],
            subtree: true
        });

        return () => {
            document.head.removeChild(style);
            document.removeEventListener("pointerdown", onPointerDown, true);
            observer.disconnect();
        };
    }, []);

    if (loading) {
        return (
            <View style={styles.center}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    if (error || !form) {
        return (
            <View style={styles.center}>
                <Text variant="titleMedium" style={styles.errorText}>
                    {error ?? "Visit not found"}
                </Text>
                <Button
                    mode="contained"
                    icon="refresh"
                    onPress={() => {
                        setLoading(true);
                        setError(null);
                        loadForm();
                    }}
                    style={styles.retryButton}
                >
                    Retry
                </Button>
            </View>
        );
    }

    return (
        // key={form.visit.id}: give each visit its OWN FormProvider instance (and
        // therefore its own AutoSaveEngine + dirty answer buffer + answers state).
        // Expo Router reuses this `visit/[id]` screen instance across a param-only
        // A->B navigation, so without the key a single long-lived engine would
        // flush visit A's still-dirty answers to visit B. That cross-wrote a
        // 6-year-old's assessment with an elderly patient's data (incident
        // 2026-07-06). Remounting per visit makes the leak structurally impossible.
        <FormProvider key={form.visit.id} form={form} onFormChange={setForm}>
            <ContextVisibilityProvider key={form.visit.id} visitId={String(form.visit.id)}>
                <VisitDocumentViewerProvider key={form.visit.id}>
                    <Stack screenOptions={{ headerShown: false }}>
                        <Stack.Screen name="index" />
                        <Stack.Screen name="assessment" options={{ headerShown: false }} />
                        <Stack.Screen name="section/[sectionId]" options={{ headerShown: false }} />
                        <Stack.Screen name="welcome-package" options={{ headerShown: false }} />
                        <Stack.Screen name="medications" options={{ headerShown: false }} />
                    </Stack>
                </VisitDocumentViewerProvider>
            </ContextVisibilityProvider>
        </FormProvider>
    );
}

const styles = StyleSheet.create({
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 24
    },
    errorText: {
        textAlign: "center",
        marginBottom: 16,
        color: "#666"
    },
    retryButton: {
        borderRadius: 8
    }
});
